/**
 * Audience requirements: who must read and be assessed on each lesson, and
 * who only reads it. Decided once, at the course's audience step, as two groups:
 *
 * - Read & Assess (mandatory): its roles complete and are assessed on the
 *   lessons for their own capabilities, and on lessons that build no
 *   capability; they read the other roles' capability lessons.
 * - Read Only (optional): reads every lesson, to stay in the loop; never assessed.
 *
 * A learner who matches both groups is Read & Assess. Every lesson's
 * requirements follow from the groups, so nothing is edited lesson by lesson.
 *
 * The grid and the lesson summary draw a RequirementsView. With the backend
 * on (flags.ts, audienceRequirementsBackend()) it is the backend's answer, as
 * GET programs/{id}/audienceRequirements gives it (viewFromServer): the
 * groups, each lesson's capability tag, and every cell with its hover.
 *
 * DUMMY until then: the groups are made up per program by useAudienceGroups,
 * the lessons' tags by withDummyCapabilities, and every cell is computed here
 * (dummyView, readAssessAccess, readOnlyAccess). Only The Playground on a
 * development build sees any of it (`requirementsPilot`).
 */
import { CAPABILITY_RELEASE_PILOT_ORG_IDS } from '../../../../../../../../utils/constants';
import type { AudienceRequirementsResponse } from '../../../../../../../../api/audienceRequirements';

export type Access = 'readAssess' | 'readOnly';

export const ACCESS_LABEL: Record<Access, string> = { readAssess: 'Read & Assess', readOnly: 'Read Only' };
// The Insights traffic lights (insights/insightsData.ts): green, yellow.
export const ACCESS_COLOR: Record<Access, string> = { readAssess: '#22C55E', readOnly: '#EAB308' };

/** A role in the Read & Assess group, with the capabilities (by name) it holds. */
export interface GroupRole {
  id: string;
  name: string;
  capabilityNames: string[];
}

/** An audience group as a column shows it: its name, and who is in it for the hover. */
export interface ViewAudience {
  id: string;
  name: string;
  /** Who it is, list by list (Roles, then each list it narrows by), for its column's hover. */
  parts: Array<{ label: string; values: string[] }>;
}

/** One audience of a group, named as the audience step describes it ("Account Exec · New Hire"). */
export interface GroupAudience extends ViewAudience {
  /** Its roles (Read & Assess audiences always have one; Read Only may have none). */
  roles: GroupRole[];
}

export interface AudienceGroups {
  readAssess: GroupAudience[];
  /** None when the course has no Read Only group. */
  readOnly: GroupAudience[];
}

/** Every Read & Assess role, once. */
export const readAssessRoles = (g: AudienceGroups): GroupRole[] => {
  const seen = new Map<string, GroupRole>();
  g.readAssess.forEach((a) => a.roles.forEach((r) => seen.set(r.id, r)));
  return Array.from(seen.values());
};

/** The program structure as requirements read it. */
export interface ReqLesson {
  id: string;
  title: string;
  capabilityName?: string | null;
}
export interface ReqModule {
  id: string;
  title: string;
  lessons: ReqLesson[];
}

/** A Read & Assess role's access to a lesson: assessed unless the lesson builds a capability it doesn't hold. */
export const roleAccess = (role: GroupRole, lesson: ReqLesson): Access =>
  lesson.capabilityName && !role.capabilityNames.includes(lesson.capabilityName) ? 'readOnly' : 'readAssess';

/** What an audience gets on a lesson: one access for all its roles, or "byRole" when its roles differ. */
export interface AudienceAccess {
  value: Access | 'byRole';
  /** One line on why, under the audience's name in the tooltip. */
  detail: string;
  /** On a capability lesson: each of the audience's roles and what it must do. */
  roles?: Array<{ name: string; access: Access }>;
}

/** "A", "A and B", "A, B and C". */
const listed = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * A Read & Assess audience on a lesson: its roles' access, the same for all of
 * them or by role. On a capability lesson the tooltip names who the capability
 * is for (`courseRoles`: every Read & Assess role of the course), then lists
 * each of the audience's roles and what it must do.
 */
export const readAssessAccess = (a: GroupAudience, lesson: ReqLesson, courseRoles: GroupRole[]): AudienceAccess => {
  const each = a.roles.map((r) => ({ name: r.name, access: roleAccess(r, lesson) }));
  const first = each[0]?.access ?? 'readAssess';
  const value = each.every((e) => e.access === first) ? first : 'byRole';
  if (!lesson.capabilityName) {
    return {
      value,
      detail: 'This lesson builds no capability, so everyone in this group follows the same requirement:',
      roles: each
    };
  }
  const holders = courseRoles.filter((r) => r.capabilityNames.includes(lesson.capabilityName!)).map((r) => r.name);
  return {
    value,
    detail: `This lesson builds ${lesson.capabilityName}, a capability for ${
      listed(holders) || 'no role in this course'
    }, so:`,
    roles: each
  };
};

/** A Read Only audience: reads every lesson, each of its roles (or everyone in it, when it names none). */
export const readOnlyAccess = (a: GroupAudience): AudienceAccess => ({
  value: 'readOnly',
  detail: 'This audience group was added as read only, so:',
  roles: a.roles.length
    ? a.roles.map((r) => ({ name: r.name, access: 'readOnly' as const }))
    : [{ name: 'Everyone in it', access: 'readOnly' as const }]
});

/**
 * What the grid and the lesson summary draw: the audience groups (columns,
 * Read & Assess ones then Read Only ones), and each lesson under its module
 * with what every group must do on it, by the group's id.
 */
export interface RequirementsView {
  readAssess: ViewAudience[];
  readOnly: ViewAudience[];
  modules: Array<{
    id: string;
    title: string;
    lessons: Array<{ id: string; title: string; cells: Record<string, AudienceAccess> }>;
  }>;
  /**
   * The course has no groups of its own yet: the ones shown are what it would
   * get from its current audience (one Read & Assess group, PRD R9).
   */
  derived: boolean;
}

type ServerCell = AudienceRequirementsResponse['modules'][number]['lessons'][number]['cells'][string];
/** A cell as the backend words it (its roles' ids aren't drawn). */
const cellOf = (cell: ServerCell): AudienceAccess => ({
  value: cell.value,
  detail: cell.detail,
  roles: (cell.roles ?? []).map((r) => ({ name: r.name, access: r.access }))
});

/** The backend's grid, as the grid and the summary draw it. */
export const viewFromServer = (res: AudienceRequirementsResponse): RequirementsView => {
  const audience = (c: AudienceRequirementsResponse['columns'][number]): ViewAudience => ({
    id: c.id,
    name: c.name,
    parts: c.parts ?? []
  });
  return {
    readAssess: res.columns.filter((c) => c.kind === 'readAssess').map(audience),
    readOnly: res.columns.filter((c) => c.kind === 'readOnly').map(audience),
    modules: res.modules.map((m) => ({
      id: String(m.id),
      title: m.title,
      lessons: m.lessons.map((l) => ({
        id: String(l.id),
        title: l.title,
        cells: Object.fromEntries(Object.entries(l.cells ?? {}).map(([id, cell]) => [id, cellOf(cell)]))
      }))
    })),
    derived: Boolean(res.derived)
  };
};

/** DUMMY: the made-up groups and the designer's lessons, every cell computed here. */
export const dummyView = (modules: ReqModule[], groups: AudienceGroups): RequirementsView => {
  const courseRoles = readAssessRoles(groups);
  return {
    readAssess: groups.readAssess,
    readOnly: groups.readOnly,
    modules: modules.map((m) => ({
      id: m.id,
      title: m.title,
      lessons: m.lessons.map((l) => ({
        id: l.id,
        title: l.title,
        cells: Object.fromEntries([
          ...groups.readAssess.map((a) => [a.id, readAssessAccess(a, l, courseRoles)]),
          ...groups.readOnly.map((a) => [a.id, readOnlyAccess(a)])
        ])
      }))
    })),
    derived: false
  };
};

/** What an entry point opens: the course grid (header), or one lesson's summary. */
export type Scope = { kind: 'course' } | { kind: 'lesson'; moduleId: string; lessonId: string };

/**
 * DUMMY: a course whose lessons carry no capability tags gets its lessons
 * spread over the Read & Assess roles' capabilities (every fourth left
 * untagged, as a learning-outcome lesson), so the grid can be judged.
 */
export const withDummyCapabilities = (modules: ReqModule[], roles: GroupRole[]): ReqModule[] => {
  if (modules.some((m) => m.lessons.some((l) => l.capabilityName))) return modules;
  const pool = roles.flatMap((r, ri) => r.capabilityNames.slice(0, 2).map((name) => ({ name, ri })));
  if (!pool.length) return modules;
  let i = 0;
  return modules.map((m) => ({
    ...m,
    lessons: m.lessons.map((l) => {
      const n = i++;
      return n % 4 === 3 ? l : { ...l, capabilityName: pool[n % pool.length].name };
    })
  }));
};

/** Requirements are a prototype: The Playground, on a development build, only. */
export const requirementsPilot = (orgAuthId: string | null | undefined): boolean =>
  process.env.NODE_ENV === 'development' && Boolean(orgAuthId) && CAPABILITY_RELEASE_PILOT_ORG_IDS.includes(orgAuthId!);
